import { useDebouncedValue, useElementSize } from '@mantine/hooks';
import {
  createContext,
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { pick } from 'remeda';
import { hasLength, hasValue } from 'utility/functions';
import {
  CoreLogEntry,
  CoreLogLevel,
  CoreLogQueryField,
} from 'providers/contexts/extensions/CoreLogs';
import { LogViewerLabels, LogViewerRow } from './LogViewer';

/**
 * --------------------------------------------------------
 * Types / Functions / Constants for Log Search
 * --------------------------------------------------------
 */

/** Log-search filter payload (message + level + date range). */
export type LogFilters = {
  query?: string;
  level?: CoreLogLevel[];
  start_date?: string;
  end_date?: string;
};

/** Which advanced filters currently have a value applied. */
export type LogFiltersVisibility = {
  showLevel: boolean;
  showDate: boolean;
};

/** Per-caller config that scopes the modal to one log stream. */
export type LogSearchConfig = {
  /** Fixed log name (path param), e.g. `tncms_notifier`. */
  log: string;
  /** Fixed `fields` filter that scopes the log to the caller's record. */
  fixedFields?: CoreLogQueryField;
  /** Maps a raw log entry to a LogViewer row. */
  toRow: (entry: CoreLogEntry, index: number) => LogViewerRow;
  /** Optional transform of the mapped rows (eg. chronological ordering). */
  transformRows?: (rows: LogViewerRow[]) => LogViewerRow[];
  /** Show the Driver column in the grid. */
  showDriver?: boolean;
  /** Grid labels (columns / statuses / empty / etc). */
  labels: LogViewerLabels;
  /** Gate the log query (eg. false until a job id resolves). */
  enabled?: boolean;
};

/**
 * Which advanced filters are visible/applied.
 * Returns a boolean flag for each advanced filter.
 */
export function getLogAdvFiltersVisibility(filters: LogFilters): LogFiltersVisibility {
  const visibility: LogFiltersVisibility = {
    showLevel: hasLength(filters?.level),
    showDate: hasLength(filters?.start_date) || hasLength(filters?.end_date),
  };
  return visibility;
}

/**
 * Updates a single key in a filters object.
 * - Removes the key when the value is empty (null, undefined, '', or []).
 * - Otherwise sets the value as provided.
 */
type FormatFilterUpdateArgs = {
  filters: LogFilters;
  key: LogFiltersKey;
  value: string | string[];
};

export function formatFilterUpdate({
  filters,
  key,
  value,
}: FormatFilterUpdateArgs): LogFilters {
  const updated: Record<string, any> = { ...filters };
  const isFalsy = !hasValue(value);
  const isEmptyArray = Array.isArray(value) && value.length === 0;

  if (isFalsy || isEmptyArray) {
    delete updated[key];
  } else {
    updated[key] = value;
  }

  return updated as LogFilters;
}

type LogFiltersKey = keyof LogFilters;

type LogSearchContextType = LogSearchConfig & {
  defaultFilters: LogFilters;
  filters: LogFilters;
  dbFilters: LogFilters;
  setFilters: React.Dispatch<React.SetStateAction<LogFilters>>;
  stagedFilters: LogFilters;
  setStagedFilters: React.Dispatch<React.SetStateAction<LogFilters>>;
  showAdvFilters: LogFiltersVisibility;
  advFilterCount: number;
  keptKeys: LogFiltersKey[];
  clearFilters: (keepKeys?: LogFiltersKey[]) => void;
  updateFilterValue: (key: LogFiltersKey, value: string | string[]) => void;
  updateStagedFilterValue: (key: LogFiltersKey, value: string | string[]) => void;
  textInputRef: RefObject<HTMLInputElement>;
  textBoxSizeRef: RefObject<any>;
  textBoxWidth: number;
  advPopoverAnchorEl: HTMLElement | null;
  advPopoverOpen: boolean;
  setAdvPopoverAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  handleOpenAdvPopover: () => void;
  handleCloseAdvPopover: () => void;
};

const LogSearchContext = createContext<LogSearchContextType | undefined>(undefined);

export const useLogSearch = () => {
  const context = useContext(LogSearchContext);
  if (!context) {
    throw new Error('useLogSearch must be used within a LogSearchProvider');
  }
  return context;
};

const DEBOUNCE_MS = 600;

/** Preserved when clearing filters */
const KEEP_KEYS: LogFiltersKey[] = ['query'];

type LogSearchProviderProps = LogSearchConfig & {
  children: ReactNode;
  /** Start filters  */
  initialFilters?: LogFilters;
};

export const LogSearchProvider = memo(
  ({
    children,
    initialFilters,
    labels,
    log,
    toRow,
    enabled,
    transformRows,
    showDriver,
    fixedFields,
  }: LogSearchProviderProps) => {
    // Initial filters on mount.
    const [defaultFilters] = useState<LogFilters>(initialFilters || {});

    // Applied filters + debounced copy for the query.
    const [filters, setFilters] = useState<LogFilters>(initialFilters || {});
    const [dbFilters] = useDebouncedValue(filters, DEBOUNCE_MS);

    // Staged filters for the advanced popover.
    const [stagedFilters, setStagedFilters] = useState<LogFilters>(initialFilters || {});

    // Which advanced filters are visible + the applied count for the badge.
    const showAdvFilters = useMemo<LogFiltersVisibility>(() => {
      return getLogAdvFiltersVisibility(filters);
    }, [filters]);

    const advFilterCount = useMemo<number>(() => {
      return Object.values(showAdvFilters).filter(Boolean).length;
    }, [showAdvFilters]);

    // Clears filters - pass keys arr to preserve.
    const clearFilters = useCallback((keepKeys?: LogFiltersKey[]) => {
      const keysToPreserve = keepKeys || [];
      setFilters((prev) => pick(prev, keysToPreserve));
    }, []);

    const updateFilterValue = useCallback(
      (key: LogFiltersKey, value: string | string[]) => {
        setFilters((prev) => {
          return formatFilterUpdate({ filters: prev, key, value });
        });
      },
      [],
    );

    const updateStagedFilterValue = useCallback(
      (key: LogFiltersKey, value: string | string[]) => {
        setStagedFilters((prev) => {
          return formatFilterUpdate({ filters: prev, key, value });
        });
      },
      [],
    );

    // Message search input ref (focus restore on clear).
    const textInputRef = useRef<HTMLInputElement>(null);

    // Measure the input row for the advanced popover anchor + width.
    const { ref: textBoxSizeRef, width: textBoxWidth } = useElementSize<HTMLDivElement>({
      box: 'border-box',
    });

    const [advPopoverAnchorEl, setAdvPopoverAnchorEl] = useState<HTMLElement | null>(
      null,
    );
    const advPopoverOpen = Boolean(advPopoverAnchorEl);

    // Copy the applied filters into staged on open so "Apply" is a pure replace.
    const handleOpenAdvPopover = useCallback(() => {
      setAdvPopoverAnchorEl(textBoxSizeRef.current);
      setStagedFilters(filters);
    }, [filters, textBoxSizeRef]);

    const handleCloseAdvPopover = useCallback(() => {
      setAdvPopoverAnchorEl(null);
    }, []);

    const contextValue = useMemo<LogSearchContextType>(() => {
      return {
        // via config props
        labels,
        log,
        toRow,
        enabled,
        transformRows,
        showDriver,
        fixedFields,
        // Internal states
        defaultFilters,
        filters,
        dbFilters,
        setFilters,
        stagedFilters,
        setStagedFilters,
        showAdvFilters,
        advFilterCount,
        keptKeys: KEEP_KEYS,
        clearFilters,
        updateFilterValue,
        updateStagedFilterValue,
        textInputRef,
        textBoxSizeRef,
        textBoxWidth,
        advPopoverAnchorEl,
        advPopoverOpen,
        setAdvPopoverAnchorEl,
        handleOpenAdvPopover,
        handleCloseAdvPopover,
      };
    }, [
      labels,
      log,
      toRow,
      enabled,
      transformRows,
      showDriver,
      fixedFields,
      defaultFilters,
      filters,
      dbFilters,
      stagedFilters,
      showAdvFilters,
      advFilterCount,
      clearFilters,
      updateFilterValue,
      updateStagedFilterValue,
      textBoxSizeRef,
      textBoxWidth,
      advPopoverAnchorEl,
      advPopoverOpen,
      handleOpenAdvPopover,
      handleCloseAdvPopover,
    ]);

    return (
      <LogSearchContext.Provider value={contextValue}>
        {children}
      </LogSearchContext.Provider>
    );
  },
);

LogSearchProvider.displayName = 'LogSearchProvider';
